import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { TrendingDown, TrendingUp, Target, CalendarDays, LineChart as LineIcon, Info, Scale, Share2 as Share2Icon } from "lucide-react";
import TransformationCardDialog from "./TransformationCardDialog";

const TEAL = "#0D9488";
const TEAL_D = "#0F766E";
const SLATE = "#94A3B8";
const AMBER = "#F59E0B";
const MS_PER_DAY = 86_400_000;

/** Least-squares linear regression on the (t_ms, weight) pairs.
 *  Returns { slope, intercept } — slope is weight-units per millisecond. */
function linearRegression(points) {
  const n = points.length;
  if (n < 2) return null;
  const sumX = points.reduce((s, p) => s + p.t, 0);
  const sumY = points.reduce((s, p) => s + p.w, 0);
  const sumXY = points.reduce((s, p) => s + p.t * p.w, 0);
  const sumXX = points.reduce((s, p) => s + p.t * p.t, 0);
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

/**
 * Weight Insights — 30-day trend line + a live "predicted goal date" projection.
 *
 * Handles every edge case gracefully:
 *  • no logs at all           → CTA to start logging
 *  • fewer than 5 in 30 days  → "Log a few more" message
 *  • no goal set              → CTA to set one on the Weight page
 *  • trend flat (~0/day)      → "Trend is flat — keep going"
 *  • trend going wrong way    → "Your rate is heading away from goal"
 *  • already at goal          → "Goal reached 🎯"
 */
export default function WeightInsightsCard() {
  const { user } = useAuth();
  const [logs, setLogs] = useState(null); // null = loading, [] = empty
  const [shareOpen, setShareOpen] = useState(false);
  const goalWeight = user?.goal_weight;
  const unit = user?.weight_unit || "lb";

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/weights", { params: { limit: 200 } });
        if (!cancel) setLogs(Array.isArray(data) ? data : []);
      } catch {
        if (!cancel) setLogs([]);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const insights = useMemo(() => {
    if (!logs) return { status: "loading" };
    // last-30-day window, oldest → newest
    const cutoff = Date.now() - 30 * MS_PER_DAY;
    const window = logs
      .map((r) => ({ t: new Date(r.logged_at).getTime(), w: Number(r.weight) }))
      .filter((r) => Number.isFinite(r.t) && Number.isFinite(r.w) && r.t >= cutoff)
      .sort((a, b) => a.t - b.t);

    if (logs.length === 0) return { status: "no-logs" };
    if (window.length === 0) return { status: "stale" };  // has logs but none in last 30d

    // Chart series (oldest → newest, one point per unique day)
    const byDay = new Map();
    for (const p of window) {
      const day = new Date(p.t); day.setHours(0, 0, 0, 0);
      byDay.set(day.getTime(), p.w);
    }
    const chart = [...byDay.entries()].sort((a, b) => a[0] - b[0]).map(([t, w]) => ({
      t, label: new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" }), weight: w,
    }));

    const latest = chart[chart.length - 1];
    const first = chart[0];
    const delta = latest.weight - first.weight;

    if (goalWeight == null) return { status: "no-goal", chart, latest };
    if (Math.abs(latest.weight - goalWeight) < 0.1) return { status: "at-goal", chart, latest };

    if (chart.length < 5) return { status: "few-points", chart, latest };

    // Linear regression on the raw millisecond timestamps
    const reg = linearRegression(chart.map((c) => ({ t: c.t, w: c.weight })));
    if (!reg) return { status: "few-points", chart, latest };

    const perDay = reg.slope * MS_PER_DAY; // weight change per day
    const toGo = goalWeight - latest.weight;

    // Direction check: perDay must have the same sign as toGo (i.e. moving toward goal)
    const movingToward = (toGo > 0 && perDay > 0) || (toGo < 0 && perDay < 0);
    const isFlat = Math.abs(perDay) < 0.01;

    if (isFlat) return { status: "flat", chart, latest, perDay };
    if (!movingToward) return { status: "wrong-way", chart, latest, perDay, toGo, delta };

    const daysToGoal = Math.ceil(Math.abs(toGo / perDay));
    if (daysToGoal > 730) return { status: "too-far", chart, latest, perDay, daysToGoal };

    const goalDate = new Date(latest.t + daysToGoal * MS_PER_DAY);
    return {
      status: "on-track",
      chart, latest, first, perDay, toGo, delta, daysToGoal, goalDate,
    };
  }, [logs, goalWeight]);

  // ---- render helpers ----
  const wrap = (kids) => (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="weight-insights-card">
      <div className="flex items-center gap-2 mb-1">
        <LineIcon className="w-4 h-4" style={{ color: TEAL }} />
        <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL }}>
          Weight Insights
        </div>
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-teal-700 hover:text-teal-900"
          data-testid="weight-insights-share"
          title="Build a shareable transformation card"
        >
          <Share2Icon className="w-3 h-3" /> Share
        </button>
      </div>
      <h3 className="font-display font-bold text-xl text-slate-900 mb-4">30-day trend & goal projection</h3>
      {kids}
      <TransformationCardDialog open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );

  const emptyState = (title, body, cta) => (
    <div className="border border-dashed border-slate-200 rounded-lg p-6 text-center bg-slate-50/40" data-testid="weight-insights-empty">
      <Info className="w-5 h-5 mx-auto mb-2" style={{ color: SLATE }} />
      <div className="text-sm font-semibold text-slate-800 mb-1">{title}</div>
      <div className="text-xs text-slate-500 leading-relaxed max-w-xs mx-auto">{body}</div>
      {cta && (
        <Link
          to={cta.to}
          className="inline-flex items-center gap-1 mt-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0D9488] hover:text-[#0F766E]"
          data-testid="weight-insights-empty-cta"
        >
          {cta.label} →
        </Link>
      )}
    </div>
  );

  if (insights.status === "loading") {
    return wrap(<div className="h-40 rounded-lg bg-slate-100 animate-pulse" />);
  }
  if (insights.status === "no-logs") {
    return wrap(emptyState(
      "No weight logs yet",
      "Log a couple of weigh-ins and we'll draw a trend line + estimate when you'll hit your goal.",
      { to: "/weight", label: "Log first weigh-in" },
    ));
  }
  if (insights.status === "stale") {
    return wrap(emptyState(
      "Nothing in the last 30 days",
      "You've got older weigh-ins on file, but we need at least one in the past 30 days to compute a fresh trend.",
      { to: "/weight", label: "Log a fresh weigh-in" },
    ));
  }
  if (insights.status === "no-goal") {
    return wrap(
      <>
        <MiniChart chart={insights.chart} goalWeight={null} unit={unit} />
        {emptyState(
          "Set a goal weight",
          "We can't predict your goal date without a target. Pop over to the Weight page to set one — takes 5 seconds.",
          { to: "/weight", label: "Set goal weight" },
        )}
      </>
    );
  }
  if (insights.status === "few-points") {
    return wrap(
      <>
        <MiniChart chart={insights.chart} goalWeight={goalWeight} unit={unit} />
        {emptyState(
          "A few more data points needed",
          `We use linear regression on your last 30 days. Log ${5 - insights.chart.length} more weigh-in${5 - insights.chart.length === 1 ? "" : "s"} across different days for a reliable projection.`,
          { to: "/weight", label: "Log another weigh-in" },
        )}
      </>
    );
  }
  if (insights.status === "at-goal") {
    return wrap(
      <>
        <MiniChart chart={insights.chart} goalWeight={goalWeight} unit={unit} />
        <div className="mt-4 border border-teal-200 bg-teal-50 rounded-lg p-4 text-center" data-testid="weight-insights-at-goal">
          <div className="text-2xl mb-1">🎯</div>
          <div className="font-semibold text-slate-900 text-sm">Goal reached</div>
          <div className="text-xs text-slate-500 mt-0.5">Consider setting a new target or entering maintenance mode.</div>
        </div>
      </>
    );
  }
  if (insights.status === "flat" || insights.status === "wrong-way" || insights.status === "too-far") {
    const msgMap = {
      flat: { title: "Trend is flat", body: "Your weekly rate is close to zero. Adjust intake or activity to move the trajectory.", tone: "amber" },
      "wrong-way": { title: "Heading away from goal", body: `You're trending ${insights.perDay > 0 ? "up" : "down"} at ~${Math.abs(insights.perDay).toFixed(2)} ${unit}/day, but your goal is the other way.`, tone: "amber" },
      "too-far": { title: "Over 2 years at current pace", body: "Your projection is very far out. A small tweak to intake or protocol usually pulls this in fast.", tone: "amber" },
    };
    const m = msgMap[insights.status];
    return wrap(
      <>
        <MiniChart chart={insights.chart} goalWeight={goalWeight} unit={unit} />
        <div
          className="mt-4 border rounded-lg p-4 flex items-start gap-3 warn-panel"
          data-testid="weight-insights-warning"
        >
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: AMBER }} />
          <div>
            <div className="font-semibold text-sm text-slate-900">{m.title}</div>
            <div className="text-xs text-slate-600 leading-relaxed mt-0.5">{m.body}</div>
          </div>
        </div>
      </>
    );
  }

  // status === "on-track"
  const perDayAbs = Math.abs(insights.perDay);
  const isCut = insights.perDay < 0;
  const dateLabel = insights.goalDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });

  return wrap(
    <>
      <MiniChart chart={insights.chart} goalWeight={goalWeight} unit={unit} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        {/* Predicted date — headline stat */}
        <div className="sm:col-span-2 border border-teal-200 bg-teal-50/40 rounded-lg p-4" data-testid="weight-insights-eta">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-teal-700 mb-1">
            <CalendarDays className="w-3 h-3" /> Predicted goal date
          </div>
          <div className="font-display font-bold text-slate-900 text-2xl leading-tight" data-testid="weight-insights-eta-date">
            {dateLabel}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            ~{insights.daysToGoal} day{insights.daysToGoal === 1 ? "" : "s"} at your current rate.
          </div>
        </div>
        {/* Rate stat */}
        <div className="border border-slate-200 rounded-lg p-4" data-testid="weight-insights-rate">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
            {isCut ? <TrendingDown className="w-3 h-3" /> : <TrendingUp className="w-3 h-3" />} Current rate
          </div>
          <div className="font-display font-bold text-slate-900 text-2xl leading-tight tabular-nums">
            {isCut ? "−" : "+"}{perDayAbs.toFixed(2)} <span className="text-sm text-slate-500 font-normal">{unit}/day</span>
          </div>
          <div className="text-xs text-slate-500 mt-1 tabular-nums">
            {(perDayAbs * 7).toFixed(2)} {unit}/week
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
        <span className="flex items-center gap-1"><Target className="w-3 h-3" /> Goal {goalWeight}{unit}</span>
        <span className="flex items-center gap-1"><Scale className="w-3 h-3" /> {insights.latest.weight}{unit} today · {Math.abs(insights.toGo).toFixed(1)}{unit} to go</span>
      </div>
    </>
  );
}

/* --- Compact recharts trend ---------------------------------------------- */
function MiniChart({ chart, goalWeight, unit }) {
  const ymin = Math.min(...chart.map((c) => c.weight), goalWeight ?? Infinity) - 1;
  const ymax = Math.max(...chart.map((c) => c.weight), goalWeight ?? -Infinity) + 1;
  return (
    <div className="h-40 -mx-1" data-testid="weight-insights-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chart} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#64748B" }}
            interval="preserveStartEnd"
            axisLine={{ stroke: "#E2E8F0" }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#64748B" }}
            domain={[ymin, ymax]}
            width={40}
            axisLine={{ stroke: "#E2E8F0" }}
            tickLine={false}
          />
          <Tooltip
            formatter={(v) => [`${v} ${unit}`, "Weight"]}
            contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, color: "#0F172A", fontSize: 12 }}
          />
          {goalWeight != null && (
            <ReferenceLine
              y={goalWeight}
              stroke={TEAL_D}
              strokeDasharray="4 3"
              label={{ value: `Goal ${goalWeight}`, position: "insideTopRight", fill: TEAL_D, fontSize: 10 }}
            />
          )}
          <Line
            type="monotone"
            dataKey="weight"
            stroke={TEAL}
            strokeWidth={2.5}
            dot={{ r: 3, fill: TEAL, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: TEAL_D, strokeWidth: 2, stroke: "#FFFFFF" }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
